import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Car, Heart, Users, Shield, Globe } from 'lucide-react';
import CTA from '../../components/CTA';

const DungannonHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Dungannon | Online Sessions for Mid Ulster</title>
        <meta
          name="description"
          content="Professional hypnotherapy for Dungannon and South Tyrone. Online sessions available - no travel required. Anxiety, stress, chronic pain, work pressures. CNHC registered. Supporting Dungannon's diverse community."
        />
        <meta name="keywords" content="hypnotherapy Dungannon, hypnotherapist Dungannon, online hypnotherapy Mid Ulster, anxiety treatment Dungannon, stress management Dungannon, chronic pain Dungannon, therapist Dungannon, shift work stress" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/dungannon-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Dungannon | Online Sessions for Mid Ulster" />
        <meta property="og:description" content="Professional hypnotherapy for Dungannon and South Tyrone. Online sessions available. Anxiety, stress, chronic pain. Supporting Dungannon's diverse community." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Dungannon",
            "description": "CNHC registered hypnotherapy serving Dungannon and Mid Ulster via online sessions. Specializing in anxiety, stress, chronic pain management, and work-related stress. Supporting Dungannon's diverse community with accessible online therapy.",
            "url": "https://www.sharonclare-hypnotherapy.com/dungannon-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Dungannon, County Tyrone",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Stress Management",
              "Chronic Pain Management",
              "Work-Related Stress Support",
              "Smoking Cessation"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy for Dungannon
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Online sessions available for Dungannon and Mid Ulster residents
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  Online sessions available - no travel required
                </p>
              </div>
              <p className="text-gray-600">
                Our clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast (approximately 40 miles, 45-55 min drive via M1/A2). <span className="font-semibold">We strongly recommend online sessions</span> as the practical option for Dungannon residents. Online hypnotherapy is just as effective while eliminating the long rural drive.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Dungannon and South Mid Ulster
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                While I don't have a physical clinic in Dungannon, I'm proud to serve the Mid Ulster community through secure online hypnotherapy sessions. As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand that geography shouldn't be a barrier to accessing quality mental health support. At approximately 40 miles from my clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast - a 45-55 minute drive via the M1 and A2 - traveling for regular in-person sessions simply isn't practical for most Dungannon residents. That's why online sessions are the ideal solution.
              </p>
              <p>
                Online hypnotherapy via secure video call provides exactly the same professional, evidence-based treatment as face-to-face sessions, with the crucial advantage of eliminating travel time entirely. You can attend from the comfort of your own home in Dungannon, at times that fit around shift work or family responsibilities, without the burden of nearly two hours of rural driving for each appointment. For Mid Ulster communities that have historically faced barriers accessing specialist mental health services, online therapy represents a genuine solution to the healthcare access gap.
              </p>
              <p>
                Dungannon is the second-largest town in County Tyrone, with a population of over 16,000, and serves as an important administrative and commercial center for South Mid Ulster. As one of three main hubs for Mid Ulster District Council, Dungannon plays a significant role in the region. However, the town faces unique pressures that can impact mental health and wellbeing. The local economy has been heavily dependent on food processing - particularly poultry production at Pilgrim's Europe (formerly Moy Park) - which provides vital employment but also creates specific stresses: shift work patterns that disrupt sleep and family life, physical demands, and economic vulnerability when the sector faces challenges.
              </p>
              <p>
                The recent announcement of job losses at ABP Foods' Dungannon facility - 338 positions at the retail packing operation - has understandably created anxiety throughout the community. When a town's economic base is concentrated in a single sector, redundancies don't just affect those directly losing jobs; they create ripples of worry and uncertainty across the whole area. Financial stress, employment insecurity, and concerns about the future can all manifest as anxiety, sleep problems, increased stress, and other mental health challenges. This is exactly where hypnotherapy can help - providing practical tools to manage anxiety, build resilience, and maintain wellbeing even when external circumstances feel uncertain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supporting Dungannon's Diverse Community Section */}
      <section className="py-16 sm:py-20 bg-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Supporting Dungannon's Diverse Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Dungannon has the highest proportion of foreign-born residents of any town in Northern Ireland - 34.5% according to the 2021 census. This makes it one of the most culturally diverse communities in the country. Many residents from Eastern Europe, Portugal, the Philippines, and other countries have made Dungannon their home, often recruited to work in the food processing industry. This diversity is a strength, but it also creates unique challenges that can affect mental health: language barriers, cultural adjustment, separation from family and familiar support networks, and the stress of navigating life in a new country.
              </p>
              <p>
                I'm committed to providing accessible, culturally sensitive hypnotherapy support for all Dungannon residents, regardless of background. While sessions are conducted in English, the visual and experiential nature of hypnotherapy can be particularly effective for clients who are more comfortable with non-verbal therapeutic techniques. Online sessions also remove barriers related to transport, childcare, and finding time around demanding work schedules - practical considerations that can be especially significant for immigrant workers juggling multiple responsibilities and often sending money home to support family abroad.
              </p>
              <p>
                For workers on shift patterns - common in food processing - online therapy offers crucial flexibility. You don't need to worry about whether you can make a fixed appointment time after a night shift, or arrange transport when you're working irregular hours. Sessions can be scheduled around your actual availability, whether that's early morning, late evening, or mid-week days off. This flexibility makes consistent therapy genuinely achievable rather than another impossible thing to fit into an already overstretched schedule.
              </p>
              <p>
                The stresses faced by Dungannon's working community are real and legitimate: shift work disrupting sleep patterns and family routines, physically demanding jobs, economic pressures and insecurity, the challenge of making ends meet on hourly wages, and for many immigrant workers, the additional burden of supporting family back home while building a life in Northern Ireland. Anxiety, stress, sleep problems, and chronic pain related to physical work are all common issues - and all respond well to evidence-based hypnotherapy approaches.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How I Can Support Dungannon Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Shield className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Anxiety and Work-Related Stress
                    </h3>
                    <p className="text-gray-700">
                      Whether you're experiencing anxiety related to job insecurity, financial pressures, shift work demands, or general worry, hypnotherapy can help you develop calm and control. For workers facing the stress of economic uncertainty or recent redundancies, solution-focused hypnotherapy builds resilience and coping strategies that work even when circumstances feel challenging.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Heart className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Chronic Pain Management
                    </h3>
                    <p className="text-gray-700">
                      For workers in physically demanding roles - food processing, manufacturing, manual labor - chronic pain is a common issue. Back pain, repetitive strain injuries, arthritis, and work-related musculoskeletal conditions can all benefit from hypnotherapy. Research shows hypnotherapy can reduce pain intensity by 29% on average, offering relief alongside medical treatment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Sleep Problems and Shift Work
                    </h3>
                    <p className="text-gray-700">
                      Shift work wreaks havoc on sleep patterns. Rotating shifts, night work, and irregular schedules disrupt your body's natural rhythms, leading to insomnia, poor-quality sleep, and exhaustion. Hypnotherapy is highly effective for sleep issues, teaching your brain healthier sleep patterns and helping you develop relaxation techniques that work even with disrupted schedules.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Globe className="w-8 h-8 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Cultural Adjustment and Isolation
                    </h3>
                    <p className="text-gray-700">
                      For Dungannon's immigrant community, the stress of living far from family, navigating a different culture, and building a life in a new country can lead to anxiety, loneliness, and overwhelm. Hypnotherapy provides practical strategies for managing stress, building confidence, and developing resilience while adjusting to life in Northern Ireland.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Smoking Cessation for Dungannon Workers
              </h3>
              <p>
                Many workers in Dungannon smoke as a way to manage stress during breaks. However, smoking is expensive - a 20-a-day habit costs thousands of pounds a year. For families trying to make ends meet, or workers sending money home to support relatives abroad, that's a significant financial burden. Hypnotherapy for smoking cessation works by breaking the psychological addiction and stress-relief association, making it easier to quit without intense cravings or anxiety. Many motivated clients quit successfully after 3-4 sessions. Imagine what you could do with thousands of pounds a year back in your pocket - that's real money that could go toward your family, your home, your future.
              </p>

              <h3 className="text-2xl font-heading font-semibold text-gray-900 mt-8 mb-4">
                Evidence-Based Treatment You Can Trust
              </h3>
              <p>
                I'm registered with the Complementary and Natural Healthcare Council (CNHC), the UK's government-backed regulator for complementary therapies. This registration requires 450+ hours of professional training, ongoing CPD, professional indemnity insurance, and adherence to strict ethical guidelines. My 15 years as a registered nurse at Belfast City Hospital provides additional medical context and understanding of complex health conditions, work-related injuries, and the impact of shift work on health.
              </p>
              <p>
                Hypnotherapy isn't magic - it's an evidence-based therapeutic technique that works with your brain's natural neuroplasticity. Research shows it's effective for anxiety (60% reduction in symptoms), chronic pain management (29% pain reduction), sleep problems, and habit change when the client is motivated. I combine hypnotherapy with cognitive behavioral techniques, solution-focused therapy, and practical stress management strategies for comprehensive support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Resources Section */}
      <section className="py-16 sm:py-20 bg-red-50 border-l-4 border-red-600">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
              Crisis Support for Dungannon Residents
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              If you're experiencing a mental health crisis, self-harm thoughts, or suicidal ideation, please reach out for immediate support. Hypnotherapy is not appropriate for crisis situations - you need urgent professional help.
            </p>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Southern Health and Social Care Trust Crisis Response</h3>
                <p className="text-gray-700 mb-1">24/7 mental health crisis support for Mid Ulster</p>
                <a href="tel:08001837745" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  0800 183 7745
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Lifeline NI</h3>
                <p className="text-gray-700 mb-1">24/7 crisis helpline for anyone in distress or despair</p>
                <a href="tel:08088088000" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  0808 808 8000
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Samaritans</h3>
                <p className="text-gray-700 mb-1">24/7 emotional support for anyone struggling to cope</p>
                <a href="tel:116123" className="text-primary-600 font-semibold text-lg hover:text-primary-700">
                  116 123
                </a>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-gray-700">
                  <strong>In an emergency:</strong> Call 999 or go to the emergency department at South Tyrone Hospital, Dungannon.
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>GP referral:</strong> For complex mental health needs including diagnosed conditions (depression, PTSD, bipolar disorder, schizophrenia, psychosis), please speak to your GP. Hypnotherapy can support mental wellness but is not a replacement for psychiatric care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Attending Sessions from Dungannon
            </h2>

            <div className="bg-accent-100 border-l-4 border-accent-600 rounded-r-xl p-6 mb-8">
              <div className="flex items-start space-x-4">
                <Monitor className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    Online Sessions: The Practical Choice
                  </h3>
                  <p className="text-gray-700 mb-4">
                    For Dungannon clients, we <strong>strongly recommend online sessions</strong> rather than traveling to our physical clinic. Here's why:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Distance:</strong> Approximately 40 miles to the Belfast clinic (around an hour via the M1 and then the Ormeau Road) - that's roughly 2 hours of driving for a 60-minute session</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Cost:</strong> 70-80 mile round trip means the cost of fuel, plus wear on your vehicle</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Time:</strong> 2.5+ hours total commitment (travel + session) vs 60 minutes at home</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Shift Work Flexibility:</strong> Schedule sessions around irregular work patterns without worrying about travel time</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Convenience:</strong> No childcare arrangements, no time off work, sessions fit around your life</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Consistency:</strong> Easier to commit to regular sessions when travel isn't a barrier</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Equally effective:</strong> Research shows online hypnotherapy produces the same outcomes as in-person sessions</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    Online sessions use secure, GDPR-compliant video calling (Zoom or similar). You'll need a private space, stable internet connection, and a device with a camera (laptop, tablet, or smartphone). Sessions are conducted exactly as in-person appointments, with the same professionalism, privacy, and therapeutic effectiveness.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-100 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                    In-Person Sessions (Available but Not Recommended)
                  </h3>
                  <p className="text-gray-700 mb-4">
                    If you strongly prefer in-person sessions despite the distance, two clinic locations are available: the Belfast clinic on the Ormeau Road and the Lisburn clinic on Antrim Street. Days and times at both vary.
                  </p>
                  <div className="space-y-3 text-gray-700">
                    <div>
                      <p className="font-semibold">To Belfast Clinic (Belfast Wellness Clinic, 228 Ormeau Road, BT7 2FZ):</p>
                      <p>From Dungannon: approximately 40 miles via the M1 east into Belfast, then south along the Ormeau Road (A24). Around an hour depending on traffic. Clinic days and times vary, and early evening appointments are available.</p>
                    </div>
                    <div>
                      <p className="font-semibold">To Lisburn Clinic (The Arzen Collective, 13B Antrim Street, BT28):</p>
                      <p>From Dungannon: east along the M1, leaving at the Lisburn junctions — a shorter run than continuing into Belfast. Clinic days and times vary, and early evening appointments are available.</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-4 italic">
                    Honest advice: For weekly or fortnightly sessions over several weeks, the travel commitment from Dungannon isn't sustainable for most people. Online sessions offer the same quality of care with none of the travel burden.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Sessions and Packages
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Initial Consultation</h3>
                <p className="text-gray-600">90 minutes including assessment and first session</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 text-center">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">Single Session</h3>
                <p className="text-gray-600">60-minute follow-up session</p>
              </div>
              <div className="bg-accent-100 rounded-xl p-6 text-center border-2 border-accent-600">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">4-Session Package</h3>
                <p className="text-gray-600">Best value for ongoing work, at a discount on single sessions</p>
              </div>
            </div>
            <p className="text-center text-gray-600 mb-4">
              Online appointments carry no travel costs.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Common Questions from Dungannon Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are online sessions as effective as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes. Research shows online hypnotherapy produces the same therapeutic outcomes as face-to-face sessions. The hypnotic trance state is just as deep, the therapeutic techniques are identical, and the relationship between therapist and client develops equally well. Many clients actually find it easier to relax at home in their own familiar environment. The only requirement is a private space, stable internet, and a device with a camera.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can you help with anxiety about job losses and economic uncertainty?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. The recent redundancies at ABP Dungannon and ongoing concerns about employment security in the food processing sector have created significant anxiety throughout the community. Solution-focused hypnotherapy is highly effective for this type of worry and stress. Rather than dwelling on fears about the future, we focus on building resilience, developing coping strategies, and strengthening your sense of control even when external circumstances feel uncertain. Hypnotherapy can't change economic realities, but it can help you manage the anxiety and maintain wellbeing while navigating challenging times.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with sleep problems from shift work?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for sleep issues, including those caused by shift work. Rotating shifts and night work disrupt your circadian rhythm, making it hard to fall asleep or stay asleep when you need to. Hypnotherapy teaches your brain healthier sleep patterns and provides relaxation techniques that work even with irregular schedules. Many shift workers report significant improvements in sleep quality within 4-6 sessions, experiencing deeper sleep, easier time falling asleep, and waking more refreshed.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do I need to speak perfect English for hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  You need to be able to communicate in English for the discussion parts of sessions, but you don't need perfect fluency. Hypnotherapy works at a subconscious level and uses relaxation, visualization, and positive suggestions - techniques that don't rely entirely on complex language. Many clients whose first language isn't English find hypnotherapy particularly effective because it works with feelings and mental imagery rather than just talking. If you're concerned about language, we can discuss this during the free initial consultation to ensure you feel comfortable.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can online sessions work around shift patterns?
                </h3>
                <p className="text-gray-700">
                  Absolutely. This is one of the major advantages of online therapy for Dungannon's working community. We can schedule sessions around your actual availability - whether that's early morning after a night shift, late evening before you start, or on your days off. You don't need to worry about whether you can make a fixed appointment time or arrange transport when you're working irregular hours. The flexibility of online sessions makes consistent therapy genuinely achievable even with demanding work schedules.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How does hypnotherapy help with chronic pain from physical work?
                </h3>
                <p className="text-gray-700">
                  Chronic pain from physically demanding work - back pain, repetitive strain, joint problems - responds well to hypnotherapy. Pain is processed in the brain, and research shows that stress and anxiety intensify pain signals significantly. Solution-focused hypnotherapy works by calming your nervous system, reducing pain perception, and teaching your brain healthier patterns of pain processing. Most clients experience reductions in pain intensity (research shows 29% average reduction) and improvements in their ability to function despite pain. Hypnotherapy works alongside medical treatment and physiotherapy, not as a replacement.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe? Will I lose control?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is completely safe and you remain in control throughout. Hypnosis is a natural state of focused attention - similar to being absorbed in a book or film - not unconsciousness or sleep. You can't be made to do anything against your will, you'll remember everything, and you can open your eyes and end the session at any time. It's a collaborative process where you're an active participant in your own healing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies depending on your goals and circumstances. Some clients achieve their goals in 3-4 sessions (smoking cessation, specific issues), while others benefit from 6-8 sessions for more complex concerns (chronic anxiety, pain management, long-term stress). We'll discuss realistic expectations in your initial consultation and review progress regularly. There's no pressure to commit to multiple sessions - you can decide after each appointment whether to continue.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you accept health insurance?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is a private service and not covered by the NHS. However, some private health insurance policies (Benenden Health, Westfield Health, Vitality Health) include complementary therapy benefits. Check your policy details. Payment is required at the time of booking. I provide receipts that you can submit to your insurer for potential reimbursement.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can I see you if I'm taking medication for anxiety or depression?
                </h3>
                <p className="text-gray-700">
                  Yes. Hypnotherapy works alongside medication (SSRIs, SNRIs, benzodiazepines) without any negative interactions. In fact, research shows that combining hypnotherapy with medication and/or talking therapy produces better outcomes than any single treatment alone. Never stop or change medication without consulting your GP. I can work collaboratively with your doctor to provide integrated care.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              What Mid Ulster Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "Working shifts at the processing plant was destroying my sleep and my health. I was constantly exhausted and anxious. Sharon's online sessions fitted perfectly around my irregular hours - I could schedule them whenever I had time off. The hypnotherapy helped me sleep properly again even with rotating shifts. My anxiety is much better and I feel like I can cope with the work demands now."
                </p>
                <p className="text-gray-900 font-semibold">- R.P., County Tyrone</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "When the redundancies were announced I was terrified about losing my job and how I'd pay the bills. The stress was overwhelming. Sharon helped me manage the anxiety and build resilience to cope with the uncertainty. I'm in a much better place mentally now, whatever happens with work. Online sessions meant I could fit therapy around everything else without the stress of traveling."
                </p>
                <p className="text-gray-900 font-semibold">- K.M., Mid Ulster</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I moved to Dungannon from Poland for work and struggled with feeling isolated and homesick. The stress of being away from family and adjusting to a new country was really hard. Sharon's approach was sensitive and practical. The hypnotherapy helped me manage my anxiety and feel more settled. Being able to do sessions online from home made it easy to fit therapy into my busy schedule."
                </p>
                <p className="text-gray-900 font-semibold">- M.W., Mid Ulster</p>
              </div>

              <div className="bg-primary-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "Chronic back pain from years of physical work was affecting everything - my job, my mood, my sleep. Sharon's hypnotherapy approach helped me manage the pain much better. I'm not pain-free, but it's manageable now and I've reduced my medication significantly. The online format was perfect - I could attend sessions without having to drive 40 miles when I was in pain."
                </p>
                <p className="text-gray-900 font-semibold">- J.D., County Tyrone</p>
              </div>

              <div className="bg-accent-50 rounded-xl p-6">
                <p className="text-gray-700 italic mb-4">
                  "I quit smoking after 18 years with Sharon's help. Four sessions and I haven't touched a cigarette in 6 months. The money I'm saving is incredible - hundreds of pounds a month that used to literally go up in smoke. That money now goes toward my family. Best decision I ever made. Online sessions were so convenient I had no excuse not to do it."
                </p>
                <p className="text-gray-900 font-semibold">- T.S., Mid Ulster</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <CTA />
    </main>
  );
};

export default DungannonHypnotherapy;
